import { isUndefined } from '../utils'
import { Key, Scale } from './MusicConstants'

const KEY_DISPLAY_INDICES = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12]
export type KeyDisplayIndex = typeof KEY_DISPLAY_INDICES[number]

const KEY_DISPLAY_VALUES = [
  'C',
  'C#',
  'D',
  'D#',
  'E',
  'F',
  'F#',
  'G',
  'G#',
  'A',
  'A#',
  'B'
] as const
export type KeyDisplayValue = typeof KEY_DISPLAY_VALUES[number]

const KEY_DISPLAY_VALUES_VERBOSE = [
  'C',
  'C#/Db',
  'D',
  'D#/Eb',
  'E',
  'F',
  'F#/Gb',
  'G',
  'G#/Ab',
  'A',
  'A#/Bb',
  'B'
] as const
export type KeyDisplayValueVerbose = typeof KEY_DISPLAY_VALUES_VERBOSE[number]

const KEY_FLATS_MAPPING = {
  Db: 1,
  Eb: 3,
  Gb: 6,
  Ab: 8,
  Bb: 10
}
const KEY_SHARPS_MAPPING = {
  'C#': 1,
  'D#': 3,
  'F#': 6,
  'G#': 8,
  'A#': 10
}
const KEY_NATURAL_MAPPING = {
  C: 0,
  D: 2,
  E: 4,
  F: 5,
  G: 7,
  A: 9,
  B: 11
}

const MINOR_ABBREVIATIONS = ['Min', 'min', 'MIN', 'Minor', 'minor', 'MINOR']
const MAJOR_ABBREVIATIONS = ['Maj', 'maj', 'MAJ', 'Major', 'major', 'MAJOR']
const MINOR_AND_MAJOR_SHARED_ABBREVIATIONS = ['m', 'M']

/** Represents a Key and a Scale (e.g. C Major). Key can be "None". */
export class KeyAndScale {
  key: Key | undefined
  scale: Scale | undefined

  displayName() {
    if (isUndefined(this.key)) {
      return 'None'
    }

    const base = KEY_DISPLAY_VALUES[this.key]
    switch (this.scale) {
      case Scale.MINOR:
        return base + ' Minor'
      case Scale.MAJOR:
        return base + ' Major'
      case Scale.CHROMATIC:
      default:
        return base
    }
  }

  /** This displays "C#/Db". */
  displayNameVerbose() {
    if (isUndefined(this.key)) {
      return 'None'
    }

    const base = KEY_DISPLAY_VALUES_VERBOSE[this.key]
    switch (this.scale) {
      case Scale.MINOR:
        return base + ' Minor'
      case Scale.MAJOR:
        return base + ' Major'
      case Scale.CHROMATIC:
      default:
        return base
    }
  }

  /**
   * Returns a key number in range [0,36].
   *
   * We use the following schema for int key values:
   * - [0]: None
   * - [1,12] : C through B (natural)
   * - [13, 24]: C Minor through B Minor
   * - [25, 36]: C Major through B Major
   */
  toInt(): number {
    if (isUndefined(this.key)) {
      return 0
    }
    switch (this.scale) {
      case Scale.MINOR:
        return this.key + 13
      case Scale.MAJOR:
        return this.key + 25
      case Scale.CHROMATIC:
      default:
        return this.key + 1
    }
  }

  fromInt(value: number) {
    if (value > 36) {
      throw 'Key must be between 0 and 36'
    }
    if (value === 0) {
      this.key = undefined
      this.scale = undefined
    } else if (value <= 12) {
      this.key = value - 1
      this.scale = Scale.CHROMATIC
    } else if (value <= 24) {
      this.key = value - 13
      this.scale = Scale.MINOR
    } else if (value <= 36) {
      this.key = value - 25
      this.scale = Scale.MAJOR
    }
  }

  /**
   * Gets the amount you should transpose this to be the same as that.
   *
   * We match up scales as follows
   *       0  1  2  3  4  5  6  7  8  9  10  11
   * Major C  C# D  D# E  F  F# G  G# A  A#  B
   * Minor A  A# B  C C#  D  D# E  F  F# G   G#
   *       9  10 11 0 1   2  3  4  5  6  7   8
   *
   * Returns a number in range [-5, 5]
   */
  differenceTo(that: KeyAndScale): number {
    // If either this or that has no key, no need to transpose.
    if (isUndefined(this.key) || isUndefined(that.key)) {
      return 0
    }

    let targetKey = that.key

    // If the song is C Major, and the incoming stem is Minor, we want to transpose the
    // incoming stem to A Minor
    // In otherwords, targetMinorKey = majorKey + 9
    if (
      (that.scale === Scale.MAJOR || that.scale === Scale.CHROMATIC) &&
      this.scale === Scale.MINOR
    ) {
      targetKey = (that.key + 9) % 12
    }
    // If the song is A Minor, and the incoming stem is Major, we want to transpose
    // incoming stem to C Major
    // In otherwords, targetMajorKey = minorKey - 9
    else if (
      that.scale === Scale.MINOR &&
      (this.scale === Scale.MAJOR || this.scale === Scale.CHROMATIC)
    ) {
      targetKey = (that.key - 9 + 12) % 12
    }
    let transpose = (targetKey - this.key + 12) % 12
    if (transpose > 6) transpose -= 12

    return transpose
  }

  public static from(int: number | undefined): KeyAndScale {
    const keyAndScale = new KeyAndScale()
    keyAndScale.fromInt(int || 0)
    return keyAndScale
  }

  public static fromDisplayName(displayName: string): KeyAndScale {
    const [key, scale] = displayName.split(' ')
    const keyAndScale = new KeyAndScale()
    keyAndScale.key = KEY_DISPLAY_VALUES.indexOf(key as KeyDisplayValue)
    if (!keyAndScale.key) {
      keyAndScale.key = KEY_DISPLAY_VALUES_VERBOSE.indexOf(key as KeyDisplayValueVerbose)
    }

    if (scale) {
      keyAndScale.scale = Scale[scale.toUpperCase()]
    }

    return keyAndScale
  }

  /**
   * Takes a user inputted key/scale (e.g. "Dbm", "C#") and returns the correct key and scale.
   * Should be resilient to different types of inputs: "Dbm", "D#Maj".
   */
  public static keyAndScaleFromUserInput(userInput: string): KeyAndScale | undefined {
    const keyAndScale = new KeyAndScale()
    keyAndScale.key = KeyAndScale.keyFromUserInput(userInput)

    if (keyAndScale.key === undefined) {
      return undefined
    }

    keyAndScale.scale = KeyAndScale.scaleFromUserInput(userInput)
    return keyAndScale
  }

  /** Takes a user inputted key/scale (e.g. "Dbm", "C#") and returns the key. */
  private static keyFromUserInput(userInput: string): Key | undefined {
    let key: Key | undefined = undefined
    Object.keys(KEY_FLATS_MAPPING).forEach((keyFlat) => {
      if (userInput.includes(keyFlat)) {
        key = KEY_FLATS_MAPPING[keyFlat]
      }
    })
    if (!key) {
      Object.keys(KEY_SHARPS_MAPPING).forEach((keySharp) => {
        if (userInput.includes(keySharp)) {
          key = KEY_SHARPS_MAPPING[keySharp]
        }
      })
    }
    if (!key) {
      Object.keys(KEY_NATURAL_MAPPING).forEach((keyNatural) => {
        if (userInput.includes(keyNatural)) {
          key = KEY_NATURAL_MAPPING[keyNatural]
        }
      })
    }
    if (typeof key === 'undefined') {
      return undefined
    }

    return key
  }

  /** Takes a user inputted key/scale (e.g. "Dbm", "C#Maj") and returns the scale. */
  private static scaleFromUserInput(userInput: string): Scale {
    const hasMinorAbbreviation = MINOR_ABBREVIATIONS.some((displayValue) =>
      userInput.includes(displayValue)
    )
    const hasMajorAbbreviation = MAJOR_ABBREVIATIONS.some((displayValue) =>
      userInput.includes(displayValue)
    )
    const hasMinorAndMajorSharedAbbreviation = MINOR_AND_MAJOR_SHARED_ABBREVIATIONS.some(
      (displayValue) => userInput.includes(displayValue)
    )

    if (hasMinorAbbreviation) {
      return Scale.MINOR
    }

    if (hasMajorAbbreviation) {
      return Scale.MAJOR
    }

    if (hasMinorAndMajorSharedAbbreviation) {
      return userInput.includes('m') ? Scale.MINOR : Scale.MAJOR
    }

    return Scale.CHROMATIC
  }
}

export default KeyAndScale
